import React from "react";
import { motion } from "framer-motion";
import PageHero from "@/components/site/PageHero";
import { Reveal, PillButton, GlowOrb } from "@/components/primitives";
import { Icon } from "@/lib/icons";
import { useSeo } from "@/lib/useSeo";
import { PLATFORMS, IMAGES } from "@/lib/content";

export default function Platforms() {
  useSeo({
    title: "Our Platforms",
    description:
      "Explore the technology platforms operated by Vardhix Technologies, including Capital Tree Funding and upcoming digital products.",
    breadcrumb: [{ name: "Home", path: "/" }, { name: "Our Platforms", path: "/platforms" }],
  });

  const live = PLATFORMS.filter((p) => p.status === "Live");
  const coming = PLATFORMS.filter((p) => p.status !== "Live");

  return (
    <>
      <PageHero
        overline="Our Platforms"
        title="A growing portfolio of technology platforms"
        sub="Vardhix builds and operates digital platforms for the Indian market — starting with Capital Tree Funding and expanding into new products."
      />

      {/* Live platform */}
      <section className="relative py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {live.map((p) => (
            <Reveal key={p.name}>
              <div className="relative glass rounded-3xl overflow-hidden grid lg:grid-cols-2" data-testid="platform-live">
                <div className="relative min-h-[280px]">
                  <img src={IMAGES.trading} alt={`${p.name} trading evaluation dashboard`} className="absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-navy/90 lg:to-matte" />
                </div>
                <div className="p-8 md:p-12">
                  <span className="inline-flex items-center gap-2 rounded-full bg-teal/15 border border-teal/40 px-3 py-1 text-xs text-teal">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal animate-pulse-glow" /> {p.status}
                  </span>
                  <h2 className="mt-5 text-3xl md:text-4xl font-heading font-semibold text-white">{p.name}</h2>
                  <a href={p.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-teal text-sm hover:text-teal-glow">{p.display}</a>
                  <p className="mt-5 text-slate-300 leading-relaxed">{p.desc}</p>
                  <div className="mt-8">
                    <PillButton href={p.url} testid="platform-visit-cta" icon="ExternalLink">Visit Platform</PillButton>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Coming soon */}
      <section className="relative py-16 border-t border-white/10">
        <GlowOrb className="h-72 w-72 top-10 right-1/4" color="rgba(0,212,170,0.08)" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-white">Future Platforms</h2>
            <p className="mt-3 text-slate-400 max-w-xl">New technology brands and products currently in development under Vardhix Technologies.</p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coming.map((p, i) => (
              <Reveal key={p.name} delay={(i % 3) * 0.07}>
                <motion.div whileHover={{ y: -6 }} className="group relative glass rounded-2xl p-8 h-full overflow-hidden" data-testid={`platform-coming-${i}`}>
                  <div className="pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full bg-teal/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/5 border border-white/10 text-teal">
                    <Icon name="Sparkles" className="h-5 w-5" />
                  </span>
                  <span className="mt-6 block text-[11px] uppercase tracking-[0.2em] text-slate-500">{p.status}</span>
                  <h3 className="mt-2 text-xl font-heading font-semibold text-white">{p.name}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-xs text-slate-500">Platform names and concepts shown are indicative. No launch dates, licenses, or regulatory approvals are implied.</p>
        </div>
      </section>
    </>
  );
}
